import { useEffect } from 'react';
import { Observable } from 'rxjs';
import type { Action } from '../types/Action';
import type { ActionCreator } from '../types/ActionCreator';
import { action$ } from '../action$';
import { ofType, extractPayload } from '../operators';

/**
 * React hook to trigger a callback when specific actions are dispatched
 *
 * This hook subscribes to the action stream and calls the provided callback
 * whenever the specified action is dispatched. The subscription is automatically
 * cleaned up when the component unmounts.
 *
 * @example
 * ```tsx
 * // Trigger a function when a specific action is dispatched
 * useTriggerOnAction(
 *   () => closeDrawer(),
 *   closedDrawerAction
 * );
 *
 * // With payload
 * useTriggerOnAction(
 *   (userId: string) => console.log(`User ${userId} logged in`),
 *   userLoggedInAction
 * );
 * ```
 *
 * @deprecated This pattern is deprecated and will be removed in future versions.
 * It exists now only to support the `Drawer` component lifecycle management.
 *
 * @param callback The function to call when the action is dispatched
 * @param actionCreator The action creator to listen for
 * @param internalAction$ Optional action stream (defaults to global action$)
 */
export const useTriggerOnAction = <T = void>(
  callback: (payload: T) => void,
  actionCreator: ActionCreator<T>,
  internalAction$: Observable<Action<unknown>> = action$
): void => {
  useEffect(() => {
    const subscription = internalAction$
      .pipe(ofType(actionCreator), extractPayload())
      .subscribe(callback);

    return () => subscription.unsubscribe();
  }, [callback, actionCreator, internalAction$]);
};
